/**
 * AuthDriverCallback · v2.7.65+E69
 * ══════════════════════════════════════════════════════════════════════
 * Landing page after the Emergent-managed Google Auth redirect for the
 * DRIVER portal. Parses `#session_id=...` from the URL fragment, POSTs
 * it to `/api/auth/google/driver-session`, and:
 *   • On success (active driver) → stash JWT + driver in localStorage
 *     via the AuthContext.persist path and redirect to /dashboard.
 *   • On 403 pending_approval → toast the awaiting-approval copy and
 *     bounce back to /login (Driver tab).
 *   • On any other error → toast + bounce to /login.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { broadcastLogin } from "@/lib/authSync";

export default function AuthDriverCallback() {
  const nav = useNavigate();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const fragment = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
    const params = new URLSearchParams(fragment);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      toast.error("Missing session id — please try Google sign-in again");
      nav("/login", { replace: true });
      return;
    }
    api.post("/auth/google/driver-session", {}, {
      headers: { "X-Session-ID": sessionId },
    })
      .then((r) => {
        try {
          window.history.replaceState({}, document.title, "/dashboard");
        } catch (e) {
          console.warn("AuthDriverCallback history.replaceState blocked", e?.message);
        }
        const data = r.data;
        if (!data?.access_token || !data?.driver) {
          toast.error("Google sign-in failed — malformed response");
          nav("/login", { replace: true });
          return;
        }
        try {
          localStorage.setItem("rtg_token", data.access_token);
          localStorage.setItem("rtg_driver", JSON.stringify(data.driver));
          broadcastLogin("driver", { token: data.access_token, user: data.driver });
        } catch (_e) { /* private browsing / storage full */ }
        toast.success(`Welcome ${data.driver?.name?.split(" ")[0] || "driver"}!`);
        // Force a full navigation so AuthContext re-hydrates from localStorage.
        window.location.assign("/dashboard");
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 403 && typeof detail === "object" && detail?.code === "pending_approval") {
          toast.info(detail.message || "Account created. Awaiting admin approval.", { duration: 8000 });
        } else {
          const msg = typeof detail === "string" ? detail : (detail?.message || "Google sign-in failed");
          toast.error(msg);
        }
        nav("/login", { replace: true });
      });
  }, [nav]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0c0d10] text-zinc-100" data-testid="auth-driver-callback">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-amber-400"/>
        <div className="mt-3 text-sm text-zinc-400">Completing driver sign-in…</div>
      </div>
    </div>
  );
}
